import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { LockKey, EnvelopeSimple } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import SEO from "@/components/SEO";

export default function AdminLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      navigate(from, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Admin login" description="Sign in to the Finance Topia editorial admin panel." url="/admin/login" />
      <div className="min-h-[calc(100vh-120px)] flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block mb-8 group" data-testid="login-back">
            <div className="overline text-foreground/60 group-hover:text-primary transition-colors duration-200">
              ← Finance Topia
            </div>
          </Link>

          <div className="border-2 border-foreground p-8 md:p-10 bg-card">
            <div className="overline text-primary">Editorial Access</div>
            <h1 className="font-display font-black text-4xl mt-2 tracking-tight">
              Sign in<span className="italic font-normal">.</span>
            </h1>
            <p className="mt-3 text-sm text-foreground/70">
              Admin panel for Finance Topia editors. Not for readers.
            </p>

            <form onSubmit={submit} data-testid="admin-login-form" className="mt-8 space-y-5">
              <div>
                <label className="overline block mb-2">Email</label>
                <div className="flex items-center border border-foreground/25 focus-within:border-foreground h-12 px-3">
                  <EnvelopeSimple size={16} className="text-foreground/50" />
                  <input
                    data-testid="admin-login-email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@finance-topia.com"
                    className="flex-1 bg-transparent outline-none px-3 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Password</label>
                <div className="flex items-center border border-foreground/25 focus-within:border-foreground h-12 px-3">
                  <LockKey size={16} className="text-foreground/50" />
                  <input
                    data-testid="admin-login-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="flex-1 bg-transparent outline-none px-3 text-sm"
                  />
                </div>
              </div>

              {error && (
                <div data-testid="admin-login-error" className="text-sm text-destructive border border-destructive/40 bg-destructive/5 p-3">
                  {error}
                </div>
              )}

              <button
                data-testid="admin-login-submit"
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-foreground text-background font-semibold hover:bg-primary transition-colors duration-200 disabled:opacity-60"
              >
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <p className="mt-6 text-[11px] font-mono text-foreground/50 leading-relaxed">
              Sessions expire after 7 days. Signing in from a shared device? Sign out when you're done.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
